import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import newsletterService from '../../services/newsletterService';
import contactService from '../../services/contactService';
import { openCookieManager } from '../../services/cookieConsentService';
import AppAdSlot from './AppAdSlot';
import "./Footer.css";
import { ReactComponent as LogoMaclectic } from '../../assets/logo-horizontal.svg';

const SITE_NAME = process.env.REACT_APP_SITE_NAME;

const MESSAGE_MIN = 10;
const MESSAGE_MAX = 2000;
const CONTACT_SUBJECTS = [
  { value: 'support', label: 'Aide technique' },
  { value: 'billing', label: 'Facturation' },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'partnership', label: 'Partenariat' },
  { value: 'other', label: 'Autre' }
];

const NAVIGATION_LINKS = [
  { label: 'Accueil', to: '/' },
  { label: 'Mon Journal', to: '/nutrition/journal' },
  { label: 'Mes amis', to: '/friends/list' },
  { label: 'Mes objectifs', to: '/goals' },
  { label: 'Mon Profil', to: '/users/profile' },
  { label: 'Voir les offres', to: '/offers' }
];

const LEGAL_LINKS = [
  { label: 'Mentions légales', to: '/legal#mentions-legales' },
  { label: 'Confidentialité', to: '/legal#politique-confidentialite' },
  { label: 'Cookies', to: '/legal#politique-cookies' },
  { label: 'CGU', to: '/legal#conditions-utilisation' },
  { label: 'CGV', to: '/legal#conditions-vente' }
];

const SOCIAL_LINKS = {
  twitter: process.env.REACT_APP_SOCIAL_TWITTER_URL || '',
  linkedin: process.env.REACT_APP_SOCIAL_LINKEDIN_URL || '',
  github: process.env.REACT_APP_SOCIAL_GITHUB_URL || ''
};

const Footer = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactStatus, setContactStatus] = useState('idle');
  const [contactFeedback, setContactFeedback] = useState('');
  const [contactTouched, setContactTouched] = useState({
    name: false,
    email: false,
    subject: false,
    message: false
  });
  const isSyncingContactRef = useRef(false);

  const syncQueuedContactMessages = useCallback(async () => {
    if (isSyncingContactRef.current) {
      return;
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return;
    }

    isSyncingContactRef.current = true;
    try {
      const { sentCount, pendingCount } = await contactService.syncQueuedMessages();
      if (sentCount > 0) {
        setContactStatus('success');
        if (pendingCount > 0) {
          setContactFeedback(
            `${sentCount} message${sentCount > 1 ? 's' : ''} envoyé${sentCount > 1 ? 's' : ''}, ${pendingCount} encore en attente.`
          );
        } else {
          setContactFeedback(
            `${sentCount} message${sentCount > 1 ? 's' : ''} hors ligne envoyé${sentCount > 1 ? 's' : ''} automatiquement.`
          );
        }
      }
    } finally {
      isSyncingContactRef.current = false;
    }
  }, []);

  useEffect(() => {
    syncQueuedContactMessages();
  }, [syncQueuedContactMessages]);

  useEffect(() => {
    const handleOnline = () => {
      syncQueuedContactMessages();
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [syncQueuedContactMessages]);

  const handleNewsletterSubmit = async (event) => {
    event.preventDefault();
    const trimmedEmail = newsletterEmail.trim();
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);

    if (!isValidEmail) {
      setNewsletterStatus('error');
      setNewsletterMessage('Adresse email invalide.');
      return;
    }

    setNewsletterStatus('loading');
    setNewsletterMessage('');

    try {
      const data = await newsletterService.subscribe(trimmedEmail);
      setNewsletterStatus('success');
      setNewsletterMessage(data.message || 'Merci ! Vérifiez votre email pour confirmer.');
      setNewsletterEmail('');
    } catch (error) {
      setNewsletterStatus('error');
      setNewsletterMessage(error.message || 'Erreur lors de l’inscription à la newsletter.');
    }
  };

  const handleNewsletterChange = (event) => {
    setNewsletterEmail(event.target.value);
    if (newsletterStatus !== 'idle') {
      setNewsletterStatus('idle');
      setNewsletterMessage('');
    }
  };

  const trimmedContactName = contactName.trim();
  const trimmedContactEmail = contactEmail.trim();
  const trimmedContactMessage = contactMessage.trim();
  const contactMessageLength = trimmedContactMessage.length;
  const isContactEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedContactEmail);

  const contactErrors = {
    name: trimmedContactName.length < 2 ? 'Merci de renseigner votre nom.' : '',
    email: !isContactEmailValid ? 'Adresse email invalide.' : '',
    subject: !contactSubject ? 'Choisissez un sujet.' : '',
    message: contactMessageLength < MESSAGE_MIN
      ? 'Votre message est trop court.'
      : contactMessageLength > MESSAGE_MAX
        ? 'Votre message est trop long.'
        : ''
  };

  const isContactValid =
    !contactErrors.name &&
    !contactErrors.email &&
    !contactErrors.subject &&
    !contactErrors.message;

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setContactTouched({ name: true, email: true, subject: true, message: true });

    if (!isContactValid) {
      setContactStatus('error');
      setContactFeedback('Merci de corriger les champs en erreur.');
      return;
    }

    setContactStatus('loading');
    setContactFeedback('');

    try {
      const data = await contactService.send({
        name: trimmedContactName,
        email: trimmedContactEmail,
        subject: contactSubject,
        message: trimmedContactMessage
      });
      setContactStatus('success');
      setContactFeedback(
        data.message
        || (data.queued
          ? 'Message enregistré hors ligne. Envoi automatique à la reconnexion.'
          : 'Merci ! Votre message a bien été envoyé.')
      );
      setContactName('');
      setContactEmail('');
      setContactSubject('');
      setContactMessage('');
      setContactTouched({ name: false, email: false, subject: false, message: false });
    } catch (error) {
      setContactStatus('error');
      setContactFeedback(error.message || 'Impossible d\'envoyer votre message.');
    }
  };

  const handleContactChange = (setter) => (event) => {
    setter(event.target.value);
    if (contactStatus !== 'idle') {
      setContactStatus('idle');
      setContactFeedback('');
    }
  };

  const handleContactBlur = (field) => () => {
    setContactTouched((prev) => ({ ...prev, [field]: true }));
  };

  const isLoading = newsletterStatus === 'loading';
  const isContactBusy = contactStatus === 'loading';
  const showContactNameError = contactTouched.name && contactErrors.name;
  const showContactEmailError = contactTouched.email && contactErrors.email;
  const showContactSubjectError = contactTouched.subject && contactErrors.subject;
  const showContactMessageError = contactTouched.message && contactErrors.message;

  const renderLinkItem = (item) => {
    if (item.to) {
      return <Link to={item.to}>{item.label}</Link>;
    }

    return <a href={item.href}>{item.label}</a>;
  };

  return (
    <>
      <AppAdSlot placement="footer" />
      <footer className="footer">
        <div className="footer-container">
          {/* Section principale */}
          <div className="footer-main">
            {/* Branding */}
            <div className="footer-brand">
              <Link to="/" className="footer-logo-link" aria-label="Accueil">
                <LogoMaclectic className="footer-logo-svg" />
              </Link>
              <p className="tagline">Bougez, mangez, progressez.</p>
              <p className="description">
                Votre compagnon pour un suivi nutritionnel précis et une vie plus saine.
              </p>
            </div>

            {/* Navigation rapide */}
            <div className="footer-section">
              <h4>Navigation</h4>
              <ul className="footer-list footer-list-nav">
                {NAVIGATION_LINKS.map((item) => (
                  <li key={item.label}>{renderLinkItem(item)}</li>
                ))}
              </ul>
            </div>

            <div className="footer-section">
              <h4>Légal</h4>
              <ul className="footer-list footer-list-legal">
                {LEGAL_LINKS.map((item) => (
                  <li key={item.label}>{renderLinkItem(item)}</li>
                ))}
              </ul>
            </div>

          </div>

          {/* Newsletter + Contact */}
          <div className="footer-cta">
            <div className="newsletter-section">
              <div>
                <h4>Restez informé</h4>
                <p>Recevez nos conseils nutrition et nos actualités produit</p>
                <form className="newsletter-form" onSubmit={handleNewsletterSubmit} noValidate>
                  <input
                    type="email"
                    placeholder="Votre adresse email"
                    value={newsletterEmail}
                    onChange={handleNewsletterChange}
                    disabled={isLoading}
                    aria-label="Adresse email pour la newsletter"
                  />
                  <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Envoi...' : "S'abonner"}
                  </button>
                </form>
                {newsletterMessage && (
                  <p
                    className={`newsletter-message ${newsletterStatus}`}
                    role="status"
                    aria-live="polite"
                  >
                    {newsletterMessage}
                  </p>
                )}
              </div>
            </div>

            <div className="contact-section" id="contact">
              <div>
                <h4>Nous contacter</h4>
                <p>Une question, un bug ou une suggestion ? Écrivez-nous.</p>
                <form className="contact-form" onSubmit={handleContactSubmit} noValidate>
                  <div className="contact-row">
                    <div className="contact-field">
                      <label className="contact-label" htmlFor="contact-name">Nom complet</label>
                      <input
                        id="contact-name"
                        type="text"
                        placeholder="Ex. Cédric Martin"
                        value={contactName}
                        onChange={handleContactChange(setContactName)}
                        onBlur={handleContactBlur('name')}
                        disabled={isContactBusy}
                        aria-label="Votre nom"
                        aria-invalid={showContactNameError ? 'true' : 'false'}
                        aria-describedby={showContactNameError ? 'contact-name-error' : undefined}
                        autoComplete="name"
                        minLength={2}
                        maxLength={120}
                        required
                        className={showContactNameError ? 'is-invalid' : ''}
                      />
                      {showContactNameError && (
                        <span id="contact-name-error" className="contact-error">
                          {contactErrors.name}
                        </span>
                      )}
                    </div>
                    <div className="contact-field">
                      <label className="contact-label" htmlFor="contact-email">Email</label>
                      <input
                        id="contact-email"
                        type="email"
                        placeholder="vous@email.com"
                        value={contactEmail}
                        onChange={handleContactChange(setContactEmail)}
                        onBlur={handleContactBlur('email')}
                        disabled={isContactBusy}
                        aria-label="Votre email"
                        aria-invalid={showContactEmailError ? 'true' : 'false'}
                        aria-describedby={showContactEmailError ? 'contact-email-error' : undefined}
                        autoComplete="email"
                        inputMode="email"
                        required
                        className={showContactEmailError ? 'is-invalid' : ''}
                      />
                      {showContactEmailError && (
                        <span id="contact-email-error" className="contact-error">
                          {contactErrors.email}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="contact-field">
                    <label className="contact-label" htmlFor="contact-subject">Sujet</label>
                    <select
                      id="contact-subject"
                      value={contactSubject}
                      onChange={handleContactChange(setContactSubject)}
                      onBlur={handleContactBlur('subject')}
                      disabled={isContactBusy}
                      aria-label="Sujet du message"
                      aria-invalid={showContactSubjectError ? 'true' : 'false'}
                      aria-describedby={showContactSubjectError ? 'contact-subject-error' : undefined}
                      required
                      className={showContactSubjectError ? 'is-invalid' : ''}
                    >
                      <option value="" disabled>Choisir un sujet</option>
                      {CONTACT_SUBJECTS.map((subject) => (
                        <option key={subject.value} value={subject.value}>
                          {subject.label}
                        </option>
                      ))}
                    </select>
                    {showContactSubjectError && (
                      <span id="contact-subject-error" className="contact-error">
                        {contactErrors.subject}
                      </span>
                    )}
                  </div>
                  <div className="contact-field">
                    <label className="contact-label" htmlFor="contact-message">Message</label>
                    <textarea
                      id="contact-message"
                      placeholder="Décrivez votre besoin en quelques lignes."
                      value={contactMessage}
                      onChange={handleContactChange(setContactMessage)}
                      onBlur={handleContactBlur('message')}
                      disabled={isContactBusy}
                      aria-label="Votre message"
                      aria-invalid={showContactMessageError ? 'true' : 'false'}
                      aria-describedby="contact-message-hint"
                      rows={4}
                      minLength={MESSAGE_MIN}
                      maxLength={MESSAGE_MAX}
                      required
                      className={showContactMessageError ? 'is-invalid' : ''}
                    />
                    <div className="contact-hint" id="contact-message-hint">
                      Min {MESSAGE_MIN} caractères · {contactMessageLength}/{MESSAGE_MAX}
                    </div>
                    {showContactMessageError && (
                      <span className="contact-error">
                        {contactErrors.message}
                      </span>
                    )}
                  </div>
                  <div className="contact-actions">
                    <button type="submit" disabled={isContactBusy || !isContactValid}>
                      {isContactBusy ? 'Envoi...' : 'Envoyer'}
                    </button>
                    <span className="contact-note">Nous répondons sous 24-48h.</span>
                  </div>
                </form>
                {contactFeedback && (
                  <p
                    className={`contact-message ${contactStatus}`}
                    role="status"
                    aria-live="polite"
                  >
                    {contactFeedback}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Séparateur */}
          <div className="footer-separator"></div>

          {/* Section légale */}
          <div className="footer-legal">
            <div className="footer-copyright">
              <p>&copy; 2025 {SITE_NAME}. Tous droits réservés.</p>
              <p>Produit indépendant, conçu et maintenu en France.</p>
            </div>

            <div className="footer-links">
              {LEGAL_LINKS.map((item) => (
                <Link key={item.label} to={item.to}>
                  {item.label}
                </Link>
              ))}
              <button type="button" className="footer-link-button" onClick={openCookieManager}>
                Cookies
              </button>
            </div>

            <div className="footer-social">
              <div className="social-icons">
                {SOCIAL_LINKS.twitter && (
                  <a
                    href={SOCIAL_LINKS.twitter}
                    title="Twitter"
                    className="social-icon"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.46 6c-.85.38-1.78.64-2.75.76 1-.6 1.76-1.55 2.12-2.68-.93.55-1.96.95-3.06 1.17-.88-.94-2.13-1.53-3.51-1.53-2.66 0-4.81 2.16-4.81 4.81 0 .38.04.75.13 1.1-4-.2-7.57-2.11-9.94-5.02-.42.72-.66 1.55-.66 2.44 0 1.67.85 3.14 2.14 4-.79-.02-1.53-.24-2.18-.6v.06c0 2.33 1.66 4.28 3.86 4.72-.4.11-.83.17-1.27.17-.31 0-.62-.03-.92-.08.62 1.94 2.42 3.35 4.55 3.39-1.67 1.31-3.77 2.09-6.05 2.09-.39 0-.78-.02-1.17-.07 2.18 1.4 4.77 2.21 7.55 2.21 9.06 0 14.01-7.5 14.01-14.01 0-.21 0-.42-.01-.63.96-.69 1.8-1.56 2.46-2.55z" />
                    </svg>
                  </a>
                )}
                {SOCIAL_LINKS.linkedin && (
                  <a
                    href={SOCIAL_LINKS.linkedin}
                    title="LinkedIn"
                    className="social-icon"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg viewBox="0 0 24 24">
                      <path fill="currentColor" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                )}
                {SOCIAL_LINKS.github && (
                  <a
                    href={SOCIAL_LINKS.github}
                    title="GitHub"
                    className="social-icon"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg viewBox="0 0 24 24">
                      <path fill="currentColor" d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                    </svg>
                  </a>
                )}
              </div>
              <div>
                <small className="version">v2.1.0</small>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
